import { SideEnum, type CareOrder } from '@talos/kyoko';
import { useMemo } from 'react';

import { useCareOrWorkingOrder } from './useCareOrWorkingOrder';
import type { QuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';
import { useQuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';

export interface RFQSweepingPanelData {
  /** Sweep group ID used by execution/cancel flows that still call this value correlationID. */
  correlationID: string;
  /** Live quote group data used to render markets and execute/cancel sweep actions. */
  quoteSweepingData: QuoteSweepingQuoteMarkets;
  /** Side used when the content component creates its RFQSweeper state. */
  initialSide: SideEnum;
  /** Whether the user can choose between buy and sell in the sweeping panel. */
  canEditSide: boolean;
  /** Parent care or working order context needed when executing care-order-backed sweeps. */
  associatedCareOrder?: CareOrder;
}

export interface RFQSweepingPanelDataResult {
  panelData: RFQSweepingPanelData | undefined;
  isLoading: boolean;
  missingParentOrderID: string | undefined;
}

/**
 * Resolves the data that the dockview RFQ sweeping panel needs before it can render.
 *
 * The panel route only gives us a SweepGroupID. The original blotter-row popover already
 * had quote/care-order context, so the standalone panel recreates that context here and
 * keeps loading state distinct from a missing parent-order lookup.
 */
export function useRFQSweepingPanelData(sweepGroupID: string | undefined): RFQSweepingPanelDataResult {
  const quoteSweepingData = useQuoteSweepingQuoteMarkets(sweepGroupID);
  const representativeQuote = quoteSweepingData?.representativeQuote;
  const parentRFQID = representativeQuote?.ParentRFQID;
  const parentOrderResult = useCareOrWorkingOrder({ orderID: parentRFQID, tag: 'RFQSweepingPanel' });

  return useMemo(() => {
    if (!sweepGroupID || !quoteSweepingData || !representativeQuote) {
      return {
        panelData: undefined,
        isLoading: !!sweepGroupID,
        missingParentOrderID: undefined,
      };
    }

    if (parentRFQID && parentOrderResult.isLoading) {
      return {
        panelData: undefined,
        isLoading: true,
        missingParentOrderID: undefined,
      };
    }

    const associatedCareOrder = parentOrderResult.careOrder;
    if (parentRFQID && !associatedCareOrder) {
      return {
        panelData: undefined,
        isLoading: false,
        missingParentOrderID: parentRFQID,
      };
    }

    const lockedSide = associatedCareOrder?.Side ?? representativeQuote.Side;

    return {
      panelData: {
        correlationID: sweepGroupID,
        quoteSweepingData,
        // Care orders and one-sided RFQs decide the side before market quote responses become tradable.
        initialSide: lockedSide ?? SideEnum.Buy,
        canEditSide: lockedSide == null,
        associatedCareOrder,
      },
      isLoading: false,
      missingParentOrderID: undefined,
    };
  }, [
    parentOrderResult.careOrder,
    parentOrderResult.isLoading,
    parentRFQID,
    quoteSweepingData,
    representativeQuote,
    sweepGroupID,
  ]);
}
